import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { delay } from "../../helper/delayresponse";

interface SmsCampaignList {
  id?: number;
  name: string;
  numbers: File[] | null;
  message: string;
  retries: number;
  schedule_time: string | null;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
  retry_minutes: number | string
}

interface SmsCampaignListState {
  smscampaignlistdata: SmsCampaignList[];
  info: {
    count: number;
    current_page: number;
    limit: number;
    total_page: number;
  } | null;
  error: string | null;
  loading: boolean;
}

// ====================-SmsCampgain-listing-action-start==============
export const GetallSmsCampaignList = createAsyncThunk<
  {
    data: SmsCampaignList[];
    info: {
      count: number;
      current_page: number;
      limit: number;
      total_page: number;
    };
  },
  { search: string; page: number; limit: number; sms_campaign_id: number },
  { rejectValue: string }
>(
  "GetallSmsCampaignList",
  async ({ search, page, limit, sms_campaign_id }, { rejectWithValue }) => {
    try {
      await delay(1000)
      const response = await axiosInstance.get(`api/admin/sms-campaign/lists`, {
        params: { search, page, limit, sms_campaign_id },
      });
      return { data: response.data.data, info: response.data.info };
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// ====================SmsCampgain-listing-action-start==============

// ==================AddSmsCampaigns-action-start===========

export const AddSmsCampaignsList = createAsyncThunk<
  SmsCampaignList,
  { sms_campaign_id: number; addData: SmsCampaignList }
>(
  "smscampaignlistdata/AddSmsCampaignsList",
  async ({ sms_campaign_id, addData }) => {
    try {
      const formData = new FormData();
      formData.append("sms_campaign_id", sms_campaign_id.toString());
      formData.append("name", addData.name);
      formData.append("message", addData.message);
      formData.append("schedule_time", addData.schedule_time!);
      formData.append("retries", addData.retries.toString());
      formData.append("retry_minutes", addData.retry_minutes.toString());

      if (addData.numbers)
        addData.numbers.forEach((file) => {
          formData.append("numbers", file);
        });

      const { data } = await axiosInstance.post(
        "api/admin/sms-campaign/lists/create",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return error.response.data || "An error occurred";
      }
      return error.message;
    }
  }
);

// ==================AddSmsCampaigns-action-end===========

// =================UpdateSmsCampaigns-action-start=========
export const UpdateSmsCampaignsList = createAsyncThunk<
  SmsCampaignList,
  { sms_campaign_id: number; id: number; updatedData: SmsCampaignList },
  { rejectValue: string }
>(
  "smscampaignlistdata/UpdateSmsCampaignsList",
  async ({ id, sms_campaign_id, updatedData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("sms_campaign_id", sms_campaign_id.toString());
      formData.append("name", updatedData.name);
      formData.append("message", updatedData.message);
      formData.append("schedule_time", updatedData.schedule_time!);
      formData.append("retries", updatedData.retries.toString());
      formData.append("retry_minutes", updatedData.retry_minutes.toString());

      if (updatedData.numbers)
        updatedData.numbers.forEach((file) => {
          formData.append("numbers", file);
        });

      const response = await axiosInstance.put(
        `api/admin/sms-campaign/lists/update/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error during API call:", error);
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

// =================UpdateSmsCampaigns-action-end=========

// =================Update-smscampaign-status-action-start=============
export const UpdateSmsCampaignsStatus = createAsyncThunk<
  SmsCampaignList,
  { id: number; status: number },
  { rejectValue: string }
>(
  "smscampaignlistdata/UpdateSmsCampaignsStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `api/admin/sms-campaign/lists/update/status/${id}`,
        { status }
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// =================Update-smscampaign-status-action-end=============

// ================DeleteSmsCampaign-action-start=========
export const DeleteSmsCampaignList = createAsyncThunk<
  { id: number },
  { id: number }
>("smscampaignlistdata/DeleteSmsCampaignList", async ({ id }) => {
  try {
    const { data } = await axiosInstance.delete(
      `api/admin/sms-campaign/lists/${id}`
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return error.message;
  }
});
// ================DeleteSmsCampaign-action-end=========

const initialState: SmsCampaignListState = {
  smscampaignlistdata: [],
  info: null,
  error: null,
  loading: false,
};

const smscampaignlistSlice = createSlice({
  name: "Smscampaignlist",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===============get-all-smtp-slice-start============
      .addCase(GetallSmsCampaignList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetallSmsCampaignList.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: SmsCampaignList[];
            info: {
              count: number;
              current_page: number;
              limit: number;
              total_page: number;
            };
          }>
        ) => {
          state.loading = false;
          state.smscampaignlistdata = action.payload.data;
          state.info = action.payload.info;
        }
      )
      .addCase(
        GetallSmsCampaignList.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
    // ===============get-all-smtp-slice-end============

    // =================add-=smtp-slice-start============
    builder
      .addCase(AddSmsCampaignsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        AddSmsCampaignsList.fulfilled,
        (state, action: PayloadAction<SmsCampaignList>) => {
          state.loading = false;
          state.smscampaignlistdata.push(action.payload);
        }
      )
      .addCase(
        AddSmsCampaignsList.rejected,
        (state, action: any) => {
          state.loading = false;
          state.error = action.payload || "Failed to add smscampaignlist";
        }
      );
    // =================add-=smtp-slice-end============

    // =================Update-smtp-slice-start============
    builder
      .addCase(UpdateSmsCampaignsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateSmsCampaignsList.fulfilled,
        (state, action: PayloadAction<SmsCampaignList>) => {
          state.loading = false;
          const index = state.smscampaignlistdata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smscampaignlistdata[index] = action.payload;
          }
        }
      )
      .addCase(
        UpdateSmsCampaignsList.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update smscampaignlist";
        }
      );
    // =================Update-smtp-slice-end============

    // =================Update-smscampaign-status-slice-start============
    builder
      .addCase(UpdateSmsCampaignsStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateSmsCampaignsStatus.fulfilled,
        (state, action: PayloadAction<SmsCampaignList>) => {
          state.loading = false;
          const index = state.smscampaignlistdata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smscampaignlistdata[index] = action.payload;
          }
        }
      )
      .addCase(
        UpdateSmsCampaignsStatus.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update smscampaignlist";
        }
      );
    // =================Update-smscampaign-status-slice-end============

    // =================Delete-smtp-slice-start============
    builder
      .addCase(DeleteSmsCampaignList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        DeleteSmsCampaignList.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          state.loading = false;
        }
      )
      .addCase(DeleteSmsCampaignList.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete smscampaignlist";
      });
    // =================Delete-smtp-slice-end============
  },
});

export default smscampaignlistSlice.reducer;
